import React, { useState } from "react";
import axios from "axios";
import { useLanguage } from "../i18n/LanguageContext";
import { API, IMG } from "../lib/constants";
import TrustStrip from "../components/TrustStrip";
import { Check, Loader2 } from "lucide-react";
import SEOHead from "../components/SEOHead";

const BedRentals = () => {
    const { t, lang } = useLanguage();
    const r = t.bedRentals;
    const studioImages = [IMG.bedStation4, IMG.bedStation5, IMG.bedStation3, IMG.bedStation1, IMG.bedStation2, IMG.studio1, IMG.studio2];
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        instagram: "",
        experience_years: "",
        license_number: "",
        rental_type: "half_day",
        services_offered: "",
        message: "",
    });
    const [status, setStatus] = useState({ submitting: false, success: false, error: "" });

    const onChange = (e) =>
        setForm({ ...form, [e.target.name]: e.target.value });

    const onSubmit = async (e) => {
        e.preventDefault();
        setStatus({ submitting: true, success: false, error: "" });
        try {
            await axios.post(`${API}/bed-rental-inquiries`, { ...form, language: lang });
            setStatus({ submitting: false, success: true, error: "" });
            setForm({
                name: "", email: "", phone: "", instagram: "",
                experience_years: "", license_number: "",
                rental_type: "half_day", services_offered: "", message: "",
            });
        } catch (err) {
            setStatus({ submitting: false, success: false, error: t.common.errorSubmit });
        }
    };

    return (
        <>
            <SEOHead page="bedRentals" />
            {/* Hero */}
            <section
                data-testid="bed-rentals-hero"
                className="relative min-h-[60vh] flex items-end overflow-hidden"
            >
                <div className="absolute inset-0">
                    <img src={IMG.bedRentalHero} alt="PMU bed rental studio space Las Vegas for permanent makeup artists" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#3D3D24] via-[#3D3D24]/65 to-[#3D3D24]/20" />
                </div>
                <div className="relative w-full max-w-[1400px] mx-auto px-6 pb-14 md:pb-20 z-10">
                    <div className="max-w-3xl">
                        <div className="font-label text-[11px] text-[#D4A95A] mb-5">{r.heroOverline}</div>
                        <h1 className="font-serif text-[#FFFFF3] text-5xl md:text-7xl leading-[0.98] mb-6">
                            {r.heroTitle}
                        </h1>
                        <p className="text-[#FFFFF3]/90 text-base md:text-lg max-w-2xl leading-relaxed">
                            {r.heroSub}
                        </p>
                    </div>
                </div>
            </section>

            <TrustStrip variant="dark" />

            {/* Details */}
            <section className="bg-[#FFFFF3] py-24 md:py-32">
                <div className="max-w-[1400px] mx-auto px-6">
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 mb-20">
                        {r.details.map((d, i) => (
                            <div
                                key={i}
                                data-testid={`bed-rental-detail-${i}`}
                                className="border border-[#3D3D24]/15 p-8 hover:border-[#3D3D24] transition-colors"
                            >
                                <div className="font-serif text-3xl text-[#D4A95A] mb-3">0{i + 1}</div>
                                <div className="font-label text-[11px] text-[#3D3D24] mb-3">{d.label}</div>
                                <p className="text-sm text-[#3D3D24]/80 leading-relaxed">{d.body}</p>
                            </div>
                        ))}
                    </div>

                    {/* Studio Gallery */}
                    <div className="mb-20">
                        <div className="font-label text-[11px] text-[#D4A95A] mb-5">Our Space</div>
                        <h2 className="font-serif text-3xl md:text-4xl text-[#3D3D24] leading-[1.05] mb-8">
                            {lang === "es" ? "El Estudio" : "The Studio"}
                        </h2>
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                            {studioImages.map((src, i) => (
                                <div key={i} className="overflow-hidden aspect-square bg-[#F5F2E4]">
                                    <img
                                        src={src}
                                        alt={`Studio ${i + 1}`}
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Inquiry Form */}
                    <div className="grid lg:grid-cols-[1fr_1.4fr] gap-14 lg:gap-20 max-w-[1200px] mx-auto">
                        <div>
                            <div className="font-label text-[11px] text-[#D4A95A] mb-5">Inquire</div>
                            <h2 className="font-serif text-4xl md:text-5xl text-[#3D3D24] leading-[1.05] mb-6">
                                {r.formTitle}
                            </h2>
                            <p className="text-[#3D3D24]/80 leading-relaxed">{r.formNote}</p>
                        </div>

                        <form
                            data-testid="bed-rental-form"
                            onSubmit={onSubmit}
                            className="space-y-5 bg-[#F5F2E4] p-8 md:p-10"
                        >
                            {status.success && (
                                <div
                                    data-testid="bed-rental-success"
                                    className="bg-[#3D3D24] text-[#FFFFF3] p-4 flex items-center gap-3 text-sm"
                                >
                                    <Check size={18} className="text-[#D4A95A]" />
                                    {t.common.thankYou}
                                </div>
                            )}
                            {status.error && (
                                <div data-testid="bed-rental-error" className="bg-red-50 border border-red-200 text-red-700 p-4 text-sm">
                                    {status.error}
                                </div>
                            )}

                            <div className="grid sm:grid-cols-2 gap-4">
                                <Field testid="bed-rental-name" label="Name" name="name" value={form.name} onChange={onChange} required />
                                <Field testid="bed-rental-email" label="Email" type="email" name="email" value={form.email} onChange={onChange} required />
                                <Field testid="bed-rental-phone" label="Phone" name="phone" value={form.phone} onChange={onChange} required />
                                <Field testid="bed-rental-instagram" label="Instagram" name="instagram" value={form.instagram} onChange={onChange} />
                                <Field testid="bed-rental-experience" label="Years of Experience" name="experience_years" value={form.experience_years} onChange={onChange} />
                                <Field testid="bed-rental-license" label="License # (NV)" name="license_number" value={form.license_number} onChange={onChange} />
                            </div>

                            <div>
                                <Label>Rental Type</Label>
                                <select
                                    data-testid="bed-rental-type"
                                    name="rental_type"
                                    value={form.rental_type}
                                    onChange={onChange}
                                    className="w-full bg-[#FFFFF3] border border-[#3D3D24]/20 px-4 py-3 text-[#3D3D24] focus:border-[#3D3D24] outline-none"
                                >
                                    <option value="half_day">Half-Day — $50</option>
                                    <option value="full_day">Full-Day — $75</option>
                                    <option value="weekly">Weekly (capped at $200)</option>
                                    <option value="monthly">Monthly</option>
                                </select>
                            </div>

                            <div>
                                <Label>Services You Offer</Label>
                                <input
                                    data-testid="bed-rental-services"
                                    type="text"
                                    name="services_offered"
                                    value={form.services_offered}
                                    onChange={onChange}
                                    placeholder="e.g., Powder brows, Lashes, Microblading"
                                    className="w-full bg-[#FFFFF3] border border-[#3D3D24]/20 px-4 py-3 text-[#3D3D24] focus:border-[#3D3D24] outline-none"
                                />
                            </div>

                            <div>
                                <Label>Additional Notes</Label>
                                <textarea
                                    data-testid="bed-rental-message"
                                    name="message"
                                    value={form.message}
                                    onChange={onChange}
                                    rows={4}
                                    className="w-full bg-[#FFFFF3] border border-[#3D3D24]/20 px-4 py-3 text-[#3D3D24] focus:border-[#3D3D24] outline-none resize-none"
                                />
                            </div>

                            <button
                                data-testid="bed-rental-submit"
                                type="submit"
                                disabled={status.submitting}
                                className="w-full bg-[#3D3D24] text-[#FFFFF3] py-4 font-label text-xs hover:bg-[#D4A95A] hover:text-[#3D3D24] transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
                            >
                                {status.submitting ? <><Loader2 size={14} className="animate-spin" /> {t.common.submitting}</> : t.common.submit}
                            </button>
                        </form>
                    </div>
                </div>
            </section>
        </>
    );
};

const Label = ({ children }) => (
    <label className="font-label text-[10px] text-[#3D3D24]/70 mb-2 block">{children}</label>
);

const Field = ({ testid, label, name, value, onChange, type = "text", required }) => (
    <div>
        <Label>{label}{required && <span className="text-[#D4A95A] ml-1">*</span>}</Label>
        <input
            data-testid={testid}
            type={type}
            name={name}
            value={value}
            onChange={onChange}
            required={required}
            className="w-full bg-[#FFFFF3] border border-[#3D3D24]/20 px-4 py-3 text-[#3D3D24] focus:border-[#3D3D24] outline-none"
        />
    </div>
);

export default BedRentals;
